import { Fragment, useMemo } from 'react'
import { X } from 'lucide-react'
import { AppLink } from '@/components/ui/app-link'
import {
  buildTimelineAxis,
  type TimelineMonth,
} from '@/lib/utils/timeline-axis'
import type { EngagementTimelineRow } from '@/components/resource-availability/EngagementTimelineWaterfall'
import type { Granularity } from '@/types/ui/resource-availability'
import { weekGroupHeader } from '@/lib/utils/availability-utils'
import { useLanguage } from '@/contexts/language-context'
import { cn } from '@/lib/utils'

export interface TeamTimelineWorkerRow {
  userProfileId: number
  workerName: string
  workerInitials: string
  workerRole?: string
  /** Empty array means the worker has no active engagements in window. */
  engagements: EngagementTimelineRow[]
}

interface TeamTimelineWaterfallProps {
  workers: TeamTimelineWorkerRow[]
  /** Axis buckets — monthly or weekly depending on the section's granularity toggle. */
  months: TimelineMonth[]
  /** Bucket granularity. Drives a two-tier header (month names spanning week columns)
   *  when weekly, mirroring the heatmap's pattern. Defaults to 'monthly'. */
  granularity?: Granularity
  onRemoveWorker?: (userProfileId: number) => void
  /** Hard cap for axis extensions. When set (e.g. by a view-range filter) the chart
   *  won't extend past this date even if engagements run further out — bars beyond
   *  this point clip at the right edge instead of triggering quarter extensions. */
  axisMaxEndIso?: string
}

const LABEL_COL_WIDTH_REM = 14
// Sub-bar height: thick enough that the rounded corners read cleanly (a too-thin bar gets
// pinched by the corner radius and renders inconsistently across rows). Vertical spacing
// is handled by justify-around on the inner stack so it scales with the row height.
const SUB_BAR_HEIGHT_PX = 9
const SUB_BAR_MIN_GAP_PX = 3

interface WorkerColorScheme {
  /** Solid fill for the worker-active bar. */
  darkBg: string
  /** Hover variant of darkBg — must paint slightly darker for affordance. */
  darkHover: string
  /** Avatar background + foreground tints, paired so contrast holds. */
  avatarBg: string
  avatarText: string
}

// Static class list so Tailwind picks them up at build time. Cycles when the user has more
// selected workers than palette entries — ≥9 workers in a single team view is rare enough
// that repeating colors is acceptable.
const WORKER_COLORS: WorkerColorScheme[] = [
  { darkBg: 'bg-blue-500',    darkHover: 'hover:bg-blue-600',    avatarBg: 'bg-blue-100',    avatarText: 'text-blue-700' },
  { darkBg: 'bg-emerald-500', darkHover: 'hover:bg-emerald-600', avatarBg: 'bg-emerald-100', avatarText: 'text-emerald-700' },
  { darkBg: 'bg-violet-500',  darkHover: 'hover:bg-violet-600',  avatarBg: 'bg-violet-100',  avatarText: 'text-violet-700' },
  { darkBg: 'bg-orange-500',  darkHover: 'hover:bg-orange-600',  avatarBg: 'bg-orange-100',  avatarText: 'text-orange-700' },
  { darkBg: 'bg-pink-500',    darkHover: 'hover:bg-pink-600',    avatarBg: 'bg-pink-100',    avatarText: 'text-pink-700' },
  { darkBg: 'bg-cyan-500',    darkHover: 'hover:bg-cyan-600',    avatarBg: 'bg-cyan-100',    avatarText: 'text-cyan-700' },
  { darkBg: 'bg-amber-500',   darkHover: 'hover:bg-amber-600',   avatarBg: 'bg-amber-100',   avatarText: 'text-amber-700' },
  { darkBg: 'bg-teal-500',    darkHover: 'hover:bg-teal-600',    avatarBg: 'bg-teal-100',    avatarText: 'text-teal-700' },
]

// Single neutral grey used for every engagement-window overlay. The worker-coloured active
// bar paints on top, so the grey is only visible at the edges where the engagement extends
// past the worker's stint — letting one shared legend entry explain it for all workers.
const ENGAGEMENT_WINDOW_BG = 'bg-slate-300/70'

/**
 * Multi-worker Gantt: each selected worker gets one row containing a stacked set of thin
 * sub-bars (one per active engagement) over a shared time axis. Each worker is assigned a
 * distinct palette color (cycling past 8 selections) so rows are visually separable at a
 * glance. The goal is glanceable team availability — gaps in a row mean that worker has
 * free capacity during those months.
 */
export function TeamTimelineWaterfall({
  workers,
  months,
  granularity = 'monthly',
  onRemoveWorker,
  axisMaxEndIso,
}: TeamTimelineWaterfallProps) {
  const { dictionary } = useLanguage()
  const tTimeline = dictionary?.resourceAvailability?.timeline
  const tTeam = dictionary?.resourceAvailability?.team
  const tHeatmap = dictionary?.resourceAvailability?.heatmap

  const maxEndIso = useMemo(() => {
    let max: string | null = null
    for (const w of workers) {
      for (const e of w.engagements) {
        if (max === null || e.engagementEnd > max) max = e.engagementEnd
      }
    }
    if (axisMaxEndIso && (max === null || max > axisMaxEndIso)) {
      return axisMaxEndIso
    }
    return max
  }, [workers, axisMaxEndIso])

  const axis = useMemo(() => buildTimelineAxis(months, maxEndIso), [months, maxEndIso])

  // In weekly mode, group consecutive month-kind cells (each = one week) by their
  // start-of-month so the top header tier renders one month name spanning N week
  // columns. Quarter-kind extension cells stay separate (they span both header tiers).
  const isWeekly = granularity === 'weekly'
  interface WeekMonthGroup {
    yearMonth: string
    startIso: string // first cell's startIso, used to pick the month abbreviation
    startCol: number // 1-based axis index of the first cell (gridColumn = startCol + 1)
    endCol: number   // 1-based axis index of the last cell (inclusive)
    isCurrentMonth: boolean
  }
  const monthGroups: WeekMonthGroup[] = useMemo(() => {
    if (!isWeekly) return []
    const groups: WeekMonthGroup[] = []
    axis.cells.forEach((c, i) => {
      if (c.kind !== 'month') return
      const ym = c.startIso.slice(0, 7)
      const last = groups[groups.length - 1]
      if (last && last.yearMonth === ym && last.endCol === i - 1 + 1) {
        last.endCol = i + 1
        if (c.isCurrentMonth) last.isCurrentMonth = true
      } else {
        groups.push({
          yearMonth: ym,
          startIso: c.startIso,
          startCol: i + 1,
          endCol: i + 1,
          isCurrentMonth: c.isCurrentMonth,
        })
      }
    })
    return groups
  }, [axis.cells, isWeekly])

  // Year groups span every month-kind cell of the same calendar year. Mirrors the heatmap's
  // year tier — gives users an unambiguous read of the year context, especially across
  // multi-year windows. Quarter extension cells are excluded (they're rendered as
  // row-spanning cells covering all header tiers).
  interface YearGroup {
    year: string
    startCol: number // 1-based axis index of first cell
    endCol: number   // 1-based axis index of last cell (inclusive)
  }
  const yearGroups: YearGroup[] = useMemo(() => {
    const groups: YearGroup[] = []
    axis.cells.forEach((c, i) => {
      if (c.kind !== 'month') return
      const year = c.startIso.slice(0, 4)
      const last = groups[groups.length - 1]
      if (last && last.year === year && last.endCol === i) {
        last.endCol = i + 1
      } else {
        groups.push({ year, startCol: i + 1, endCol: i + 1 })
      }
    })
    return groups
  }, [axis.cells])

  // Axis indexes that mark the first cell of a new year (after the first year). Drives a
  // heavier left border on every header tier AND the per-worker axis-cell backgrounds, so
  // the year divider reads as one continuous line through the chart instead of stopping
  // after the year tier.
  const yearBoundaryAxisIdx: Set<number> = useMemo(() => {
    const set = new Set<number>()
    for (let i = 1; i < yearGroups.length; i++) {
      set.add(yearGroups[i].startCol - 1)
    }
    return set
  }, [yearGroups])

  if (workers.length === 0 || axis.cells.length === 0) return null

  const sortedWorkers = [...workers].sort((a, b) => a.workerName.localeCompare(b.workerName))

  // Enforce a minimum width per cell so the chart starts overflowing (and the wrapper's
  // overflow-x-auto kicks in) once columns would otherwise squish below readability. Match
  // the heatmap's per-granularity floors: 80px for monthly, 100px for weekly. Quarter
  // extension cells keep their compressed `fr` width so they don't hog a full month/week's
  // worth of horizontal space when an engagement extends past the visible window.
  const cellMinPx = isWeekly ? 100 : 80
  const cellTemplate = axis.cells
    .map((c, i) =>
      c.kind === 'month'
        ? `minmax(${cellMinPx}px, ${axis.cellWidthsFr[i]}fr)`
        : `${axis.cellWidthsFr[i]}fr`
    )
    .join(' ')
  const tableTemplate = `${LABEL_COL_WIDTH_REM}rem ${cellTemplate}`

  // Header tier count.
  // - Monthly: year (top) + per-column month name = 2 tiers.
  // - Weekly: month-with-year-suffix + per-column day-range = 2 tiers (no separate year
  //   tier; the inline ’YY suffix on each month group carries the year, more robust against
  //   horizontal scroll than a wide year-tier span centered off-screen).
  const headerRows = 2
  const workerRowOffset = headerRows + 1 // first worker sits at gridRow = headerRows + 1
  // Row index of the month tier:
  //  - monthly: row 2 (year above on row 1 → month name on row 2).
  //  - weekly: row 1 (no year tier; month-with-year-suffix is the top row).
  const monthTierRow = isWeekly ? 1 : 2

  return (
    <div className="rounded-md border bg-card">
      {/* `overflow-x-auto` is the scroll container. The grid is the direct child so sticky
          left-0 on the worker-label cells anchors against this exact box (no intervening
          padded wrapper that'd offset the sticky position). Top/bottom padding moves to the
          grid itself; left/right "padding" is applied per-cell via padding-left on row 1
          items so the sticky cell can sit flush against the scroll-container's left edge. */}
      <div className="overflow-x-auto">
        {/* `pb-3` only (no top padding on the grid) so the year-tier cell — which carries
            the heavier left-2px year divider — extends all the way to the card's top
            edge. The cell's own `pt-3` absorbs the visual top breathing room that would
            otherwise come from the grid's padding, and the border now reads as one
            continuous line from the top of the card down through every data row. */}
        <div className="grid pb-3 pr-4 min-w-fit" style={{ gridTemplateColumns: tableTemplate }}>
        {/* ---- Worker label header ---- spans every header tier. `sticky left-0` keeps it
            anchored to the scroll-container's left edge during horizontal scroll; bg-card +
            z-20 + a right border-shadow obscure scrolling content as it crosses underneath.
            Critical: `self-end` was previously collapsing this cell to its text height at the
            bottom of the span, leaving row-1 transparent so month-group labels (APR/MAY)
            from column 2+ would visibly leak through during horizontal scroll. Stretching
            the cell across both header rows (`flex items-end pb-1`) makes bg-card paint the
            full span, so header content actually disappears behind the worker column. */}
        <div
          className="pl-4 pr-3 pb-1 flex items-end sticky left-0 bg-card z-20 shadow-[1px_0_0_0_rgb(0_0_0_/_0.05)]"
          style={{ gridColumn: 1, gridRow: `1 / ${headerRows + 1}` }}
        >
          <span className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
            {tHeatmap?.workerHeader || 'Worker'}
          </span>
        </div>

        {/* ---- Top header tier: year groups — monthly only. Weekly mode skips this tier;
            the ’YY suffix on each month-group label carries the year inline. ----
            `pt-3` absorbs the grid-level top padding we moved off the grid, so the cell
            extends to the card's top edge and the year divider line runs from the very
            top down. `pb-1.5` adds breathing room below the year text before the month
            tier picks up. `flex items-end justify-center` keeps the text snug against the
            month-tier with the extra space living above it, near the card's top. ---- */}
        {!isWeekly && yearGroups.map((g, gi) => (
          <div
            key={`y-${g.year}-${g.startCol}`}
            className={cn(
              'flex items-end justify-center text-[11px] tracking-wide font-semibold text-muted-foreground pt-3 pb-1.5',
              gi > 0 && 'border-l-2 border-foreground/30'
            )}
            style={{ gridColumn: `${g.startCol + 1} / ${g.endCol + 2}`, gridRow: 1 }}
          >
            {g.year}
          </div>
        ))}

        {/* ---- Month tier (weekly only): month-name spans across the weeks of that month
            and is THE top header row in weekly (the year tier is dropped because the inline
            ’YY suffix on each label carries year context more reliably under horizontal
            scroll). `pt-3 pb-1.5` mirrors the year tier's spacing in monthly so the visual
            top margin stays consistent across granularities. Year-boundary months get the
            heavier divider so the year line stays continuous from the top of the card down
            through every data row. ---- */}
        {isWeekly && monthGroups.map((g, gi) => {
          const startAxisIdx = g.startCol - 1
          const isYearBoundary = yearBoundaryAxisIdx.has(startAxisIdx)
          const groupCount = g.endCol - g.startCol + 1
          const { label, yearSuffix } = weekGroupHeader(g.startIso, groupCount)
          return (
            <div
              key={`mg-${g.yearMonth}-${g.startCol}`}
              className={cn(
                'text-center text-[11px] uppercase tracking-wide font-semibold text-muted-foreground pt-3 pb-1.5',
                gi > 0 && (isYearBoundary
                  ? 'border-l-2 border-foreground/30'
                  : 'border-l border-foreground/15'),
                g.isCurrentMonth && 'text-foreground'
              )}
              style={{ gridColumn: `${g.startCol + 1} / ${g.endCol + 2}`, gridRow: monthTierRow }}
            >
              {label}
              {yearSuffix && (
                <span className="ml-1 text-[10px] font-normal text-muted-foreground/70 normal-case tracking-normal">
                  ’{yearSuffix}
                </span>
              )}
              {g.isCurrentMonth && (
                <span className="ml-1 text-[8px] opacity-60 normal-case tracking-normal">
                  {tHeatmap?.nowLabel || 'NOW'}
                </span>
              )}
            </div>
          )
        })}

        {/* ---- Quarter extension cells: span every header tier (year + month + day in
            weekly, year + month in monthly) so the compressed-quarter visual stays
            unbroken at the right edge of the chart. ---- */}
        {axis.cells.map((cell, i) => {
          if (cell.kind !== 'quarter') return null
          const isFirstQuarter = i === axis.firstQuarterIndex && axis.firstQuarterIndex > 0
          return (
            <div
              key={`q-${cell.startIso}`}
              className={cn(
                'text-center text-[9px] uppercase tracking-wide text-muted-foreground/70 px-0.5 pb-1 bg-muted/30 flex flex-col items-center justify-center',
                isFirstQuarter ? 'border-l border-foreground/25' : 'border-l border-foreground/10'
              )}
              style={{ gridColumn: i + 2, gridRow: `1 / ${headerRows + 1}` }}
            >
              {cell.label}
            </div>
          )
        })}

        {/* ---- Bottom header tier: per-cell labels (day-range in weekly, month name in
            monthly). Quarter cells render above as row-spanning extension cells; skip here.
            With the year tier added above, the previous "'26" sublabel for monthly columns
            is redundant — the year is now read from the top tier. ---- */}
        {axis.cells.map((cell, i) => {
          if (cell.kind === 'quarter') return null
          // Weekly: gridRow 2 (month=1, day-range=2). Monthly: gridRow 2 (year=1, month=2).
          // Always 2 now that weekly drops the dedicated year tier.
          const tierRow = 2
          const isYearBoundary = yearBoundaryAxisIdx.has(i)
          // Sublabel only shows NOW for the current month/week — the year context lives in
          // the top tier so the per-cell row stays compact.
          const showNow = cell.isCurrentMonth
          return (
            <div
              key={`h-${cell.startIso}`}
              className={cn(
                'text-[10px] uppercase tracking-wide text-muted-foreground text-center px-0.5 pb-1',
                i > 0 && (isYearBoundary
                  ? 'border-l-2 border-foreground/30'
                  : 'border-l border-foreground/10'),
                cell.kind === 'month' && cell.isCurrentMonth && !isWeekly && 'text-foreground font-semibold',
                isWeekly && 'tracking-normal text-[10px] tabular-nums'
              )}
              style={{ gridColumn: i + 2, gridRow: tierRow }}
            >
              {cell.label}
              {showNow && !isWeekly && (
                <div className="text-[8px] opacity-60 normal-case tracking-normal">
                  {tHeatmap?.nowLabel || 'NOW'}
                </div>
              )}
            </div>
          )
        })}

        {/* ---- Worker rows ---- */}
        {sortedWorkers.map((worker, rowIdx) => {
          const gridRow = rowIdx + workerRowOffset
          const engagementCount = worker.engagements.length
          const isFullyAvailable = engagementCount === 0
          const color = WORKER_COLORS[rowIdx % WORKER_COLORS.length]

          // Sort engagements by worker actual start so concurrent ones stack predictably
          // top-down in chronological order.
          const sortedEngagements = [...worker.engagements].sort((a, b) => {
            const as = a.workerStart ?? a.engagementStart
            const bs = b.workerStart ?? b.engagementStart
            return as.localeCompare(bs)
          })

          return (
            <Fragment key={worker.userProfileId}>
              <div
                className="border-t border-foreground/10"
                style={{ gridColumn: '1 / -1', gridRow }}
                aria-hidden="true"
              />

              {/* Label cell — vertical padding sets the floor on row height so single-
                  engagement rows still get breathing room around the avatar. `sticky left-0`
                  pins it during horizontal scroll; bg-card + z-10 + right shadow obscure
                  scrolling content as it crosses behind the worker column. The pl-4
                  matches the header's left padding so the avatar sits flush at the same
                  inset both stuck and unstuck. */}
              <div
                className="pl-4 pr-3 py-2 flex items-center gap-2 min-w-0 sticky left-0 bg-card z-10 shadow-[1px_0_0_0_rgb(0_0_0_/_0.05)]"
                style={{ gridColumn: 1, gridRow }}
                title={worker.workerName}
              >
                <span className={cn(
                  'inline-flex items-center justify-center w-7 h-7 rounded-full text-[10px] font-semibold shrink-0',
                  color.avatarBg,
                  color.avatarText
                )}>
                  {worker.workerInitials}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-medium leading-tight truncate">{worker.workerName}</div>
                  <div className="text-[10px] text-muted-foreground leading-tight truncate">
                    {isFullyAvailable
                      ? (tTeam?.fullyAvailable || 'Fully available')
                      : engagementCount === 1
                        ? `${engagementCount} ${tTeam?.engagementSingular || 'engagement'}`
                        : `${engagementCount} ${tTeam?.engagementPlural || 'engagements'}`}
                  </div>
                </div>
                {onRemoveWorker && (
                  <button
                    type="button"
                    onClick={() => onRemoveWorker(worker.userProfileId)}
                    aria-label={(tTeam?.removeWorkerAria || 'Remove {name} from team timeline').replace('{name}', worker.workerName)}
                    className="p-0.5 rounded hover:bg-muted-foreground/10 text-muted-foreground hover:text-foreground shrink-0"
                  >
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>

              {/* Axis cell backgrounds (dividers + quarter tint). Year-boundary cells get
                  the heavier border-l-2 so the year divider runs continuously from the year
                  tier above all the way down through every worker's row. */}
              {axis.cells.map((cell, i) => {
                const isFirstQuarter = i === axis.firstQuarterIndex && axis.firstQuarterIndex > 0
                const isYearBoundary = yearBoundaryAxisIdx.has(i)
                return (
                  <div
                    key={`${worker.userProfileId}-${cell.startIso}`}
                    className={cn(
                      i > 0 && (isYearBoundary ? 'border-l-2' : 'border-l'),
                      isYearBoundary
                        ? 'border-foreground/30'
                        : isFirstQuarter
                          ? 'border-foreground/25'
                          : cell.kind === 'quarter'
                            ? 'border-foreground/15'
                            : 'border-foreground/10',
                      cell.kind === 'quarter' && 'bg-muted/40'
                    )}
                    style={{ gridColumn: i + 2, gridRow }}
                    aria-hidden="true"
                  />
                )
              })}

              {/* Sub-bars (one per engagement). The grid stretches this cell to the row
                  height; flex-col + justify-around then distributes equal space above the
                  first bar, between each, and below the last. rowGap enforces a minimum
                  separation so bars never touch when the row is at content height. */}
              <div
                className="relative pointer-events-none flex flex-col justify-around py-2"
                style={{ gridColumn: '2 / -1', gridRow, rowGap: SUB_BAR_MIN_GAP_PX }}
              >
                  {sortedEngagements.map(eng => {
                    const lighter = axis.rangePct(eng.engagementStart, eng.engagementEnd)
                    const workerStartIso = eng.workerStart ?? eng.engagementStart
                    const workerEndIso = eng.workerEnd ?? eng.engagementEnd
                    const darker = axis.rangePct(workerStartIso, workerEndIso)
                    return (
                      <div
                        key={`${worker.userProfileId}-${eng.engagementId}`}
                        className="relative"
                        style={{ height: SUB_BAR_HEIGHT_PX }}
                      >
                        {lighter.width > 0 && (
                          <div
                            className={cn('absolute top-0 bottom-0 rounded', ENGAGEMENT_WINDOW_BG)}
                            style={{ left: `${lighter.left}%`, width: `${lighter.width}%` }}
                          />
                        )}
                        {darker.width > 0 && (
                          <AppLink
                            to={`/${eng.engagementId}`}
                            title={
                              eng.engagementName
                                ? eng.budgetedHoursLabel !== undefined || eng.budgetedHoursPerDay !== undefined
                                  ? `${eng.engagementName} · ${eng.budgetedHoursLabel ?? `${eng.budgetedHoursPerDay}h/day`}`
                                  : eng.engagementName
                                : undefined
                            }
                            className={cn(
                              'absolute top-0 bottom-0 rounded transition-colors pointer-events-auto',
                              color.darkBg,
                              color.darkHover
                            )}
                            style={{ left: `${darker.left}%`, width: `${darker.width}%` }}
                            aria-label={(tTeam?.openEngagementAria || 'Open engagement {name}').replace('{name}', eng.engagementName ?? eng.engagementId)}
                          >
                            <span className="sr-only">{eng.engagementName ?? eng.engagementId}</span>
                          </AppLink>
                        )}
                      </div>
                    )
                  })}
              </div>
            </Fragment>
          )
        })}
        </div>
      </div>

      <div className="mx-4 mb-4 pt-2 border-t border-dashed flex flex-wrap items-center gap-x-4 gap-y-1 text-[10px] text-muted-foreground">
        <div className="flex items-center gap-1.5">
          <span className={cn('inline-block w-3 h-3 rounded-sm', ENGAGEMENT_WINDOW_BG)} />
          <span>{tTimeline?.engagementWindowLegend || 'Engagement window'}</span>
        </div>
      </div>
    </div>
  )
}

